import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import { createAppQueryClient } from './queryClient';
import { applyEnvFavicon } from './utils/envFavicon';

applyEnvFavicon();

// The React Query defaults live in ./queryClient.js, so a test can run a page under the cache
// settings the app really uses (5-minute staleTime, no focus refetch, one mutation retry).
const queryClient = createAppQueryClient();

/**
 * ⚡ Performance: Register service worker for caching (if available)
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.warn('SW registration failed:', error);
    });
  });
}

/**
 * ⚡ Performance: Preload critical assets
 * Note: Gabarito is loaded from Google Fonts (see index.html); Cascadia Mono is self-hosted from /fonts
 */
const preloadCriticalAssets = () => {
  // Font preloading removed - using Google Fonts (see index.html)
  // Critical assets can be added here as needed
};

// Only preload in production
if (import.meta.env.PROD) {
  preloadCriticalAssets();
}

/**
 * ⚡ Performance: Report Web Vitals
 */
const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && typeof onPerfEntry === 'function') {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    }).catch(() => {
      // web-vitals not installed, skip
    });
  }
};

// Report vitals in development
if (import.meta.env.DEV) {
  reportWebVitals(console.log);
}

/**
 * ⚡ Render application with optimized configuration
 */
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {/* Only include DevTools in development */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom-right"
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  </React.StrictMode>
);

// Export for testing
export { queryClient };
